package com.devsusana.portfolio.data

import com.devsusana.portfolio.models.Experience

/**
 * Career timeline and technical expertise data.
 */
object ExperienceData {
    val careerSummary = """
        Senior Android Engineer and Tech Lead with 7+ years of experience building and maintaining
        production mobile systems in demanding environments like banking and high-scale consumer products.
        Specialized in Kotlin, Jetpack Compose, robust Clean Architecture, and legacy system recovery.
        Pioneering practical Agentic Programming workflows that integrate AI agents directly into
        daily mobile development to boost code quality, consistency, and automated architectural audits.
    """.trimIndent()

    val skillsList = listOf(
        "Android SDK & Kotlin",
        "Jetpack Compose & Material 3",
        "Clean Architecture & MVVM",
        "Kotlin Multiplatform (KMP)",
        "Kotlin Coroutines & Flow",
        "Dependency Injection (Hilt)",
        "Room DB & Offline-First",
        "Legacy Codebase Modernization",
        "Agentic AI & LLMs (Gemini API)",
        "Unit & Instrumented Testing",
        "CI/CD (Jenkins, GitHub Actions)",
        "iOS (Swift)",
        "Firebase (Auth, Firestore, Hosting)"
    )

    val milestones = listOf(
        Experience(
            role = "Software Engineer Mobile II",
            company = "Babel (Sevilla, España)",
            period = "2026 - Present",
            summary = "Developing native Android applications using Kotlin and Jetpack Compose following Clean Architecture principles.",
            highlights = listOf(
                "Develop Android applications using Kotlin and Jetpack Compose, following Clean Architecture principles.",
                "Collaborate with cross-functional teams to deliver high-quality features and optimize performance.",
                "Ensure code quality through testing, code reviews, and best development practices."
            ),
            technologies = listOf("Kotlin", "Jetpack Compose", "Clean Architecture", "Testing", "Coroutines")
        ),
        Experience(
            role = "Senior Android Engineer / Tech Lead",
            company = "Qaroní (Jerez de la Frontera, España)",
            period = "2024 - 2026",
            summary = "Technical leadership in complex Android applications, architectural decision-making, and legacy modernization.",
            highlights = listOf(
                "Technical leadership in complex Android applications and architectural decision-making.",
                "Recovery and stabilization of legacy projects through progressive refactoring and structural improvements.",
                "Implementation of Clean Architecture, MVVM, and state management in production systems.",
                "Mentoring junior and mid-level developers, improving code quality and review practices.",
                "Development of new features using Kotlin and Jetpack Compose in live products."
            ),
            technologies = listOf("Kotlin", "Jetpack Compose", "Clean Architecture", "MVVM", "Legacy Recovery", "Mentoring")
        ),
        Experience(
            role = "Senior Android Engineer",
            company = "Devoteam (Jerez de la Frontera, España)",
            period = "2023 - 2024",
            summary = "Native Android and Kotlin Multiplatform development across client and strategic product initiatives.",
            highlights = listOf(
                "Native Android development using Kotlin and Java across product and client projects.",
                "Implementation of Jetpack Compose and Kotlin Multiplatform in production apps.",
                "Application of Clean Architecture and modern reactive patterns in complex codebases.",
                "Work in agile, cross-functional teams delivering real-world mobile products."
            ),
            technologies = listOf("Kotlin", "Jetpack Compose", "KMP", "Clean Architecture", "Java")
        ),
        Experience(
            role = "Senior Android Engineer",
            company = "NTT Data Europe & Latam (CaixaBankNow)",
            period = "2020 - 2023",
            summary = "Development and maintenance of large-scale banking mobile application (CaixaBankNow).",
            highlights = listOf(
                "Development and maintenance of large-scale banking mobile application (CaixaBankNow).",
                "Implementation of MVP, MVVM, and Clean Architecture in complex legacy systems.",
                "Integration with CI/CD pipelines using Jenkins, GitHub, and AppCenter.",
                "Technical mentoring of junior developers and stabilization of critical banking flows."
            ),
            technologies = listOf("Kotlin", "Java", "Banking Apps", "Clean Architecture", "CI/CD", "Jenkins")
        ),
        Experience(
            role = "Mobile Developer (Android / iOS)",
            company = "Ecommerce Farm (Jerez de la Frontera, España)",
            period = "2019 - 2020",
            summary = "Development and maintenance of real-time sports betting applications for Android and iOS.",
            highlights = listOf(
                "Development and maintenance of a sports betting application for Android and iOS.",
                "Android stack: MVVM, Retrofit, Firebase, RxJava, real-time features.",
                "iOS development using Swift with MVC/MVVM patterns."
            ),
            technologies = listOf("Kotlin", "Swift", "Android", "iOS", "Firebase", "RxJava")
        ),
        Experience(
            role = "Agentic Programming Pioneer & Creator",
            company = "HomeTutorPro & Independent Research",
            period = "2024 - Present",
            summary = "Designing autonomous AI audit suites, local Gemini orchestrators, and on-device Edge AI workflows.",
            highlights = listOf(
                "Engineered a local 5-agent audit orchestrator powered by Gemini models to validate Android code against strict architectural rules.",
                "Implemented resilient rate-limit handling with exponential backoff and test mocks for free-tier LLM integrations.",
                "Automated repetitive engineering tasks: KDoc autocompletion, Hilt module injection verification, and JUnit test generation."
            ),
            technologies = listOf("Gemini API", "Python", "Multi-Agent Systems", "Prompt Engineering", "CI Automation")
        )
    )
}
